import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from './useAuth';
import { isActiveSubscription, type Subscription } from '@/types';

export interface UseSubscriptionResult {
  subscription: Subscription | null;
  isActive: boolean;
  loading: boolean;
}

/**
 * Real-time subscription state from /users/{uid}.subscription.
 * Updates immediately when the Stripe webhook writes via admin SDK.
 */
export function useSubscription(): UseSubscriptionResult {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        const sub = (data?.['subscription'] as Subscription | undefined) ?? null;
        setSubscription(sub);
        setLoading(false);
      },
      (err) => {
        console.error('useSubscription snapshot error', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user, authLoading]);

  return {
    subscription,
    isActive: isActiveSubscription(subscription),
    loading: authLoading || loading,
  };
}
